import {isMobile} from '../../../libs/shared/core/src/helpers/environment/mobileEnvHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {reportTraceEvent, TraceEventTypesEnum} from '../../../src/services/trace';
import {buildDesktopAppLoadData} from './appLoadHelpersDesktop';
import {buildMobileAppLoadData} from './appLoadHelpersMobile';
import {performance} from './performanceClient';

/*
 * Types.
 */

export enum AppLoadMetricsEnum {
  APP_RENDER = 'app_render',
  BOOT_FETCH = 'boot_fetch',
  OFFLINE_STATE_LOAD = 'offline_state_load',
  ONLINE_BOOT_FETCH = 'online_boot_fetch',
  BOOT_DATA_MERGE = 'boot_data_merge',
}

type AppLoadState = {
  [K in AppLoadMetricsEnum]?: number;
};

/*
 * Logger.
 */

const appLoadLogger = logger.getLogger('app-load');

/*
 * State.
 */

const appLoadState: AppLoadState = {};
let wasBootIncremental: boolean = false;
let bootDeltaAge: number | undefined;
let didReportMetrics: boolean = false;

/*
 * Api.
 */

export function logAppLoadMetric(name: AppLoadMetricsEnum) {
  const startTime = performance.now();
  return (isAppLoaded?: boolean) => {
    const endTime = performance.now();
    appLoadState[name] = endTime - startTime;
    if (isAppLoaded) {
      maybeReportAppLoad();
    }
  };
}

export function logAppLoadBootWasIncremental() {
  wasBootIncremental = true;
}

export function logAppLoadBootDeltaAge(newAge: number) {
  bootDeltaAge = newAge;
}

function maybeReportAppLoad() {
  if (didReportMetrics) {
    // This code path is expected on mobile (with OTA updates, or new sign-in after a sign-out)
    // In that case, we don't want to send the metrics but no need to report an error.
    if (!isMobile()) {
      reportToBugsnag(new Error('appLoadMetricHelpers tried reporting twice'));
    }

    return;
  }
  didReportMetrics = true;

  const appLoadData = isMobile() ? buildMobileAppLoadData() : buildDesktopAppLoadData();
  if (!appLoadData) {
    return;
  }

  appLoadLogger.info('App Load', {
    start_time: appLoadData.startTime,
    app_fetch_duration: appLoadData.appFetchDuration,
    boot_fetch_duration: appLoadState[AppLoadMetricsEnum.BOOT_FETCH],
    app_render_duration: appLoadState[AppLoadMetricsEnum.APP_RENDER],
    offline_state_load_duration: appLoadState[AppLoadMetricsEnum.OFFLINE_STATE_LOAD],
    online_boot_fetch: appLoadState[AppLoadMetricsEnum.ONLINE_BOOT_FETCH],
    is_boot_incremental: wasBootIncremental,
    boot_delta_age: bootDeltaAge,
    boot_data_merge: appLoadState[AppLoadMetricsEnum.BOOT_DATA_MERGE],
  });

  const bootLoad = appLoadState[AppLoadMetricsEnum.BOOT_FETCH];
  const appLoad = appLoadState[AppLoadMetricsEnum.APP_RENDER];
  if (!bootLoad || !appLoad) {
    return;
  }

  if (isMobile()) {
    // Only report the trace event on web.
    return;
  }

  reportTraceEvent({
    type: TraceEventTypesEnum.INITIAL_APP_LOAD,
    event: {
      timestamp: appLoadData.startTime,
      initialResponse: appLoadData.appFetchDuration,
      bootLoad,
      appLoad,
    },
  });
}
